import React, { useMemo } from 'react';
import {
  Box,
  Text,
  useToken
} from '@chakra-ui/react';
import { MAX_TEMP, MIN_TEMP } from '../../constants/checklist';

interface TemperatureVisualizerProps {
  value: number;
  min?: number;
  max?: number;
  unit?: string;
  showValue?: boolean;
}

const TemperatureVisualizer: React.FC<TemperatureVisualizerProps> = ({
  value,
  min = MIN_TEMP,
  max = MAX_TEMP,
  unit = '°C',
  showValue = true
}) => {
  // Get theme colors
  const [blue400, red400, orange400, yellow400] = useToken("colors", [
    "blue.400",
    "red.400", 
    "orange.400",
    "yellow.400"
  ]);

  // Map temperature value to color based on percentage
  const color = useMemo(() => {
    const percentage = ((value - min) / (max - min)) * 100;
    
    if (percentage < 25) return blue400;      // Cold (blue)
    if (percentage < 50) return yellow400;    // Cool (yellow)
    if (percentage < 75) return orange400;    // Warm (orange)
    return red400;                            // Hot (red)
  }, [value, min, max, blue400, red400, orange400, yellow400]);

  // Calculate fill percentage
  const fillPct = Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));

  return (
    <Box display="flex" flexDirection="column" alignItems="center" p={4}>
      {showValue && (
        <Text mb={3} fontWeight="bold" textAlign="center" fontSize="lg">
          {value}{unit}
        </Text>
      )}

      {/* Thermometer */}
      <Box display="flex" alignItems="flex-end" gap={2}>
        {/* Bulb */}
        <Box
          w="32px"
          h="32px"
          bg={color}
          borderRadius="full"
          boxShadow="lg"
          border="2px solid white"
          transition="background 300ms ease"
        />
        
        {/* Tube */}
        <Box
          position="relative"
          w="20px"
          h="120px"
          borderRadius="full"
          bg="gray.100"
          overflow="hidden"
          boxShadow="inset 0 2px 6px rgba(0,0,0,0.1)"
          border="2px solid white"
        >
          {/* Fill */}
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            height={`${fillPct}%`}
            bg={color}
            transition="height 400ms ease, background 300ms ease"
            borderRadius="full"
          />
          
          {/* Temperature markings */}
          <Box position="absolute" inset={0} pointerEvents="none">
            {[25, 50, 75].map((mark) => (
              <Box
                key={mark}
                position="absolute"
                left="50%"
                bottom={`${mark}%`}
                transform="translateX(-50%) translateY(50%)"
                w="1px"
                h="8px"
                bg="gray.400"
                opacity={0.6}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Scale labels */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        mt={2} 
        fontSize="xs" 
        color="gray.500"
        w="60px"
      >
        <Text>{min}</Text>
        <Text>{max}</Text>
      </Box>
    </Box>
  );
};

export default TemperatureVisualizer;